import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscribe, VISIBLE_SCOPES_CHANGED, type VisibleScopesChangedPayload } from '@/lib/graphql';

interface UseVisibleScopesSubscriptionOptions {
  enabled?: boolean;
}

export function useVisibleScopesSubscription(options: UseVisibleScopesSubscriptionOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const handleData = useCallback((data: VisibleScopesChangedPayload) => {
    const { scopes } = data.visibleScopesChanged;

    console.log('[useVisibleScopesSubscription] Received scope update:', scopes.length, 'scopes');

    // Directly update the query cache — no refetch round-trip needed.
    // The payload is always the real principal's scopes, so it writes to the
    // un-previewed key; a role preview keeps its own entry (CDEV-1454).
    queryClient.setQueryData(['applicationScopes', null], scopes);

    // Lens resolution depends on capabilities + scopes — re-query when scopes change
    queryClient.invalidateQueries({ queryKey: ['myLens'] });
  }, [queryClient]);

  const handleError = useCallback((error: Error) => {
    console.error('[useVisibleScopesSubscription] Subscription error:', error);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    console.log('[useVisibleScopesSubscription] Starting subscription');

    unsubscribeRef.current = subscribe<VisibleScopesChangedPayload>(
      VISIBLE_SCOPES_CHANGED,
      {},
      {
        onData: handleData,
        onError: handleError,
        onComplete: () => {
          console.log('[useVisibleScopesSubscription] Subscription completed');
        },
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        console.log('[useVisibleScopesSubscription] Cleaning up subscription');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [enabled, handleData, handleError]);
}
